/**
 * Did this form submission actually happen, or did the visitor only try?
 *
 * Split out from ajax-form-detection.ts because this is the part that makes a
 * judgement; the detector around it only listens and sends. The tests live
 * here for the same reason.
 *
 * Why this module exists at all. The detector counted a conversion on the
 * CLICK of a submit button, in the capture phase, before any validation had
 * run. On 6 August 2026 a real Contact Form 7 page proved what that means: a
 * required field was left empty, the browser refused to submit, the form stayed
 * on screen with "Please fill out this field", and we still reported an event.
 * With a form_submit rule on that form, that is somebody's Lead. It travels to
 * Smart Bidding, and a lead count that is too high looks exactly like a lead
 * count that is right.
 *
 * The two things we can know for certain are used, and nothing is invented
 * beyond them:
 *
 * 1. A form with an invalid control will be refused by the browser. Nothing
 *    was submitted, so nothing is counted.
 * 2. Form frameworks announce their own outcome. Contact Form 7 says
 *    `wpcf7mailsent` on success and `wpcf7invalid` on a rejected submission,
 *    and the others have equivalents. Where a framework tells us, we believe
 *    it over any guess of ours.
 *
 * Where neither applies (a hand-rolled AJAX form on a site with none of these
 * frameworks) the old behaviour is kept and the submission is counted. Not
 * counting would be the safer-looking choice and the wrong one: dropping a real
 * conversion is invisible, and this module exists precisely because invisible
 * is the failure mode that costs the most.
 */

/** What a framework's own event tells us about the submission. */
export type SubmissionOutcome = "success" | "failure";

/**
 * Framework events that are dispatched as real DOM CustomEvents, so a plain
 * addEventListener on document hears them. Contact Form 7 is the notable one.
 */
export const NATIVE_SUBMISSION_SIGNALS: Record<string, SubmissionOutcome> = {
  wpcf7mailsent: "success",
  wpcf7invalid: "failure",
  wpcf7spam: "failure",
  wpcf7mailfailed: "failure",
};

/**
 * Framework events that are only ever triggered through jQuery. A native
 * addEventListener never hears these, so they are reachable only when the page
 * has jQuery, which every one of these plugins already requires.
 */
export const JQUERY_SUBMISSION_SIGNALS: Record<string, SubmissionOutcome> = {
  wpformsAjaxSubmitSuccess: "success",
  gform_confirmation_loaded: "success",
  submit_success: "success",
  frmFormComplete: "success",
  nfFormSubmitResponse: "success",
};

/**
 * How long to wait for a framework to report an outcome before falling back.
 *
 * Long enough for an ordinary AJAX round trip on a slow connection, short
 * enough that the visitor is still on the page. Contact Form 7 answers in well
 * under a second locally, and a good deal slower on a busy shared host.
 */
export const SIGNAL_WAIT_MS = 2500;

/** Why we are being asked to decide. */
export type ResolveReason = "click" | "signal" | "timeout" | "unload";

export interface OutcomeInput {
  /**
   * Will the browser refuse this submission? True only when we could actually
   * establish it: a form element, constraint validation not switched off, and
   * an invalid control present.
   */
  readonly blockedByValidation: boolean;
  /** What a framework reported, when one did. */
  readonly signal: SubmissionOutcome | null;
  readonly reason: ResolveReason;
}

/** count = report a conversion, drop = report nothing, wait = not yet known. */
export type SubmissionDecision = "count" | "drop" | "wait";

export const decideSubmission = (input: OutcomeInput): SubmissionDecision => {
  // The browser is going to refuse this. Nothing was submitted, so there is
  // nothing to count, and no later signal can change that.
  if (input.blockedByValidation) return "drop";

  if (input.signal === "failure") return "drop";
  if (input.signal === "success") return "count";

  // A click on its own says only that somebody tried.
  if (input.reason === "click") return "wait";

  // Nothing told us anything. See the note at the top: counting is the choice
  // whose mistakes are visible.
  return "count";
};
